/**
 * API functions for managing dance classes
 */

import type { StudioClassSettingsResponse } from "@dnceclub/shared";
import { supabase } from "@/lib/supabase";
import { api } from "./api-client";

export type ClassStatus = 'open' | 'sold_out' | 'canceled' | 'waitlist' | 'scheduled' | 'draft';
export type RegistrationType = 'internal' | 'external';

/**
 * Class visibility type
 * - public: Visible on browse pages, accessible to everyone
 * - private: Only accessible via direct link
 */
export type ClassVisibility = 'public' | 'private';

export const CLASS_VISIBILITY_LABELS: Record<ClassVisibility, string> = {
  public: "Public",
  private: "Private",
};

export const CLASS_STATUS_LABELS: Record<ClassStatus, string> = {
  open: "Open",
  sold_out: "Sold Out",
  canceled: "Canceled",
  waitlist: "Waitlist",
  scheduled: "Scheduled",
  draft: "Draft",
};

/**
 * Check if a scheduled class's publish time has passed
 * Handles timezone-aware comparison using publish_at timestamp directly
 */
export function hasPublishTimePassed(classData: {
  publish_at: string | null;
  publish_time: string | null;
  publish_timezone?: string | null;
}): boolean {
  if (!classData.publish_at) return false;

  // If publish_time and timezone are provided, use them for precise comparison
  if (classData.publish_time && classData.publish_timezone) {
    // Format the publish_at date in the publish timezone to get the correct date
    // This is important because publish_at is stored in UTC, but represents a date
    // the user selected in their local timezone
    const publishDate = new Date(classData.publish_at);
    const dateFormatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: classData.publish_timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const dateParts = dateFormatter.formatToParts(publishDate);
    const getDatePart = (type: string) => dateParts.find(p => p.type === type)?.value || '';
    const dateStr = `${getDatePart('year')}-${getDatePart('month')}-${getDatePart('day')}`;

    // Get the current time in the publish timezone
    const now = new Date();
    const nowFormatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: classData.publish_timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    // Format current time as YYYY-MM-DD HH:MM in the publish timezone
    const nowParts = nowFormatter.formatToParts(now);
    const getNowPart = (type: string) => nowParts.find(p => p.type === type)?.value || '';
    const nowStr = `${getNowPart('year')}-${getNowPart('month')}-${getNowPart('day')} ${getNowPart('hour')}:${getNowPart('minute')}`;

    // Format target publish time as YYYY-MM-DD HH:MM
    const targetStr = `${dateStr} ${classData.publish_time}`;

    // String comparison works because both are in YYYY-MM-DD HH:MM format
    return nowStr >= targetStr;
  }

  // Fallback: just use publish_at directly as a timestamp
  const publishDateTime = new Date(classData.publish_at);
  return publishDateTime <= new Date();
}

/**
 * Get the display status for a class
 * For scheduled classes whose publish time has passed, returns 'open'
 * Otherwise returns the actual status
 */
export function getDisplayStatus(classData: {
  status: ClassStatus;
  publish_at: string | null;
  publish_time: string | null;
  publish_timezone?: string | null;
}): ClassStatus {
  if (classData.status === 'scheduled' && hasPublishTimePassed(classData)) {
    return 'open';
  }
  return classData.status;
}

/**
 * Check if a class is publicly visible
 * A class is publicly visible if:
 * - Status is open, sold_out, waitlist, or canceled
 * - OR status is scheduled but publish time has passed
 * - AND visibility is 'public' or null (for backwards compatibility)
 *
 * This is the centralized check for determining if a class should appear
 * on public browse pages and profiles.
 */
export function isClassPubliclyVisible(classData: {
  status: ClassStatus;
  visibility?: ClassVisibility | null;
  publish_at?: string | null;
  publish_time?: string | null;
  publish_timezone?: string | null;
}): boolean {
  // Check visibility first - private classes are never publicly visible
  if (classData.visibility === 'private') {
    return false;
  }

  // Draft classes are never publicly visible
  if (classData.status === 'draft') {
    return false;
  }

  // Scheduled classes are only visible if publish time has passed
  if (classData.status === 'scheduled') {
    return hasPublishTimePassed({
      publish_at: classData.publish_at || null,
      publish_time: classData.publish_time || null,
      publish_timezone: classData.publish_timezone,
    });
  }

  // All other statuses (open, sold_out, waitlist, canceled) are publicly visible
  return true;
}

/**
 * Get the scheduled label for a class (formatted date/time for when it will be published)
 * @param classData The class data with status, publish_at, publish_time, publish_timezone
 * @returns Formatted schedule string (e.g., "1/25/26 7PM EST") or undefined if not scheduled or already published
 */
export function getScheduledLabel(classData: {
  status: ClassStatus;
  publish_at: string | null;
  publish_time: string | null;
  publish_timezone: string | null;
}): string | undefined {
  // Only compute label for scheduled classes that haven't been published yet
  if (classData.status !== 'scheduled' || !classData.publish_at) {
    return undefined;
  }

  // If publish time has passed, don't show the scheduled label
  if (hasPublishTimePassed(classData)) {
    return undefined;
  }

  const publishDate = new Date(classData.publish_at);

  // If publish_time is provided, combine date and time
  if (classData.publish_time) {
    const [hours, minutes] = classData.publish_time.split(':').map(Number);
    publishDate.setHours(hours, minutes, 0, 0);
  }

  // Format: M/D/YY (e.g., 1/25/26)
  const month = publishDate.getMonth() + 1;
  const day = publishDate.getDate();
  const year = publishDate.getFullYear().toString().slice(-2);
  const dateStr = `${month}/${day}/${year}`;

  // Format time: HAM/PM without minutes if on the hour (e.g., 7PM not 7:00 PM)
  let timeStr = '';
  if (classData.publish_time) {
    const hours = publishDate.getHours();
    const minutes = publishDate.getMinutes();
    const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    timeStr = minutes === 0 ? `${hour12}${ampm}` : `${hour12}:${minutes.toString().padStart(2, '0')}${ampm}`;
  }

  // Get timezone abbreviation (e.g., EST, PST) - only if different from user's timezone
  let tzStr = '';
  if (classData.publish_timezone) {
    // Get user's current timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Only show timezone if it's different from user's current timezone
    if (classData.publish_timezone !== userTimezone) {
      // Create a date formatter to get the timezone abbreviation
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: classData.publish_timezone,
        timeZoneName: 'short'
      });
      const parts = formatter.formatToParts(publishDate);
      const tzPart = parts.find(part => part.type === 'timeZoneName');
      tzStr = tzPart?.value || '';
    }
  }

  return `${dateStr}${timeStr ? ` ${timeStr}` : ''}${tzStr ? ` ${tzStr}` : ''}`;
}

// Collaborator profile info (moved up for ClassData reference)
export interface CollaboratorProfileInfo {
  id: string;
  username: string | null;
  full_name: string | null;
  profile_photo: string | null;
}

// Extended owner profile info with payment handles (for collaborative classes)
export interface OwnerProfileInfo extends CollaboratorProfileInfo {
  venmo_handle: string | null;
  cashapp_handle: string | null;
  zelle_handle: string | null;
  paypal_handle: string | null;
}

export interface ClassData {
  id: string;
  profile_id: string;
  title: string;
  location: string;
  address: string | null;
  /** Geocoded latitude of address. Null until geocoded. */
  latitude: number | null;
  /** Geocoded longitude of address. Null until geocoded. */
  longitude: number | null;
  /** Timestamp of last successful geocode. Null until geocoded. */
  geocoded_at: string | null;
  room: string | null;
  room_paid_only: boolean;
  date: string; // ISO date string
  start_time: string; // HH:MM format
  end_time: string; // HH:MM format
  timezone: string;
  price: number | null;
  registration_url: string | null;
  card_payment_enabled: boolean;
  venmo_enabled: boolean;
  cashapp_enabled: boolean;
  zelle_enabled: boolean;
  paypal_enabled: boolean;
  status: ClassStatus;
  /**
   * UI-facing status — what consumers should render. Equal to `status`,
   * except when `status === 'scheduled'` and the publish time has passed:
   * then it's `'open'`. Computed by the backend at the route layer
   * (`withDisplayStatus`) so the frontend never has to recompute the rule.
   * Required: every response that returns a class includes it.
   */
  display_status: ClassStatus;
  registration_type: RegistrationType;
  confirmation_message: string | null;
  /** Optional cover photo URL for the class */
  cover_photo_url: string | null;
  /** Rich text (HTML) waiver content that registrants must agree to */
  waiver: string | null;
  /** Rich text (HTML) description of the class */
  description: string | null;
  /** Scheduled publish date (for scheduled status) */
  publish_at: string | null;
  /** Scheduled publish time (for scheduled status) */
  publish_time: string | null;
  /** Timezone for scheduled publishing */
  publish_timezone: string | null;
  created_at: string;
  updated_at: string;
  /** Studio ID if this is a studio-managed class (null for instructor-owned classes) */
  studio_id: string | null;
  /** Rate ID if this class uses a studio rate */
  rate_id: string | null;
  /** Rate info for class pricing (from class_rates table) */
  rate_info?: { id: string; name: string; price: number } | null;
  /** True if the current user is a collaborator on this class (not the owner) */
  isCollaborator?: boolean;
  /** Collaborators on this class (for display on owned classes) */
  collaborators?: CollaboratorProfileInfo[];
  /** Owner profile info with payment handles (for collaborative classes - the person who created the class) */
  ownerProfile?: OwnerProfileInfo;
  /** Number of confirmed registrations for this class (only for internal registration type) */
  registration_count?: number;
  /** Class visibility - public (browse visible) or private (link-only) */
  visibility: ClassVisibility | null;
  /** Collab invite record ID (for collaborator self-removal) */
  collabInviteId?: string;
  /** Series ID if this class belongs to a series */
  series_id?: string | null;
  /** Owner of the series this class belongs to (populated by discover endpoint) */
  series_owner_id?: string | null;
  /** Maximum number of registrations allowed */
  capacity: number | null;
  /** Whether to automatically close registration at capacity */
  auto_close_at_capacity: boolean;
  /** How to count students toward capacity: all registered or only paid */
  capacity_count_method: 'registered' | 'paid' | null;
  /** Action to take when capacity is reached: waitlist or sold_out */
  capacity_close_action: 'waitlist' | 'sold_out' | null;
  /** Pricing type — fixed price or sliding scale */
  pricing_type: 'fixed' | 'sliding_scale';
  /** Minimum price for sliding scale classes (null for fixed) */
  min_price: number | null;
  /** Maximum price for sliding scale classes (null for fixed) */
  max_price: number | null;
  /** Whether this class is managed by a studio (vs instructor-owned) */
  is_studio_managed: boolean;
  /** Guest instructor name (no linked profile) */
  guest_instructor_name: string | null;
  /** Substitute guest instructor name (no linked profile) */
  substitute_guest_name: string | null;
  /** Whether class videos are published (visible to students) */
  videos_published?: boolean;
  /** Room info from the rooms join */
  room_info?: { id: string; name: string; capacity: number | null; photos: string[] | null } | null;
  /** Studio info from the studios join */
  studio_info?: { id: string; name: string; handle: string | null; logo_url: string | null } | null;
  /** Class type ID if this class belongs to a studio class type */
  class_type_id?: string | null;
  /** Series info (populated when the class belongs to a series, by list and detail endpoints) */
  series?: {
    id: string;
    title: string;
    venmo_enabled?: boolean;
    cashapp_enabled?: boolean;
    zelle_enabled?: boolean;
    paypal_enabled?: boolean;
    card_payment_enabled?: boolean;
    profile: {
      id: string;
      username: string | null;
      full_name: string | null;
      profile_photo: string | null;
      venmo_handle?: string | null;
      cashapp_handle?: string | null;
      zelle_handle?: string | null;
      paypal_handle?: string | null;
    } | null;
  } | null;
}

export interface CreateClassInput {
  title: string;
  location: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  geocoded_at?: string | null;
  room?: string | null;
  room_paid_only?: boolean;
  date: string;
  start_time: string;
  end_time: string;
  timezone: string;
  price?: number | null;
  registration_url?: string | null;
  card_payment_enabled?: boolean;
  venmo_enabled?: boolean;
  cashapp_enabled?: boolean;
  zelle_enabled?: boolean;
  paypal_enabled?: boolean;
  status?: ClassStatus;
  registration_type?: RegistrationType;
  confirmation_message?: string | null;
  /** Optional cover photo URL for the class */
  cover_photo_url?: string | null;
  /** Rich text (HTML) waiver content that registrants must agree to */
  waiver?: string | null;
  /** Rich text (HTML) description of the class */
  description?: string | null;
  /** Scheduled publish date (for scheduled status) */
  publish_at?: string | null;
  /** Scheduled publish time (for scheduled status) */
  publish_time?: string | null;
  /** Timezone for scheduled publishing */
  publish_timezone?: string | null;
  /** Class visibility - public (browse visible) or private (link-only) */
  visibility?: ClassVisibility;
  /** Maximum number of registrations allowed */
  capacity?: number | null;
  /** Whether to automatically close registration at capacity */
  auto_close_at_capacity?: boolean;
  /** How to count students toward capacity */
  capacity_count_method?: 'registered' | 'paid' | null;
  /** Action to take when capacity is reached */
  capacity_close_action?: 'waitlist' | 'sold_out' | null;
  /** Pricing type — fixed or sliding scale */
  pricing_type?: 'fixed' | 'sliding_scale';
  /** Minimum price for sliding scale */
  min_price?: number | null;
  /** Maximum price for sliding scale */
  max_price?: number | null;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface UpdateClassInput extends Partial<CreateClassInput> {}

/**
 * Fetch all classes for a specific profile (including collaborative classes)
 * @param profileId Profile UUID
 * @param upcomingOnly If true, only return future classes (ordered oldest to newest);
 *                     if false, return past classes (ordered newest to oldest)
 * @param includeUnpublished If true, include draft and scheduled classes; if false, only show published classes
 * @returns Array of class data (owned classes + collaborative classes)
 */
export async function fetchClassesForProfile(
  profileId: string,
  upcomingOnly: boolean = true,
  includeUnpublished: boolean = false
): Promise<ClassData[]> {
  const today = new Date().toISOString().split("T")[0];

  // 1. Fetch owned classes (with rate_info for pricing, series cover photo fallback, room photos)
  let ownedQuery = supabase
    .from("classes")
    .select("*, rate_info:class_rates!rate_id(id, name, price), series:series!series_id(id, title, profile:profiles!profile_id(id, username, full_name, profile_photo)), room_info:rooms!room_id(id, name, capacity, photos)")
    .eq("profile_id", profileId);

  if (upcomingOnly) {
    ownedQuery = ownedQuery.gte("date", today);
  } else {
    ownedQuery = ownedQuery.lt("date", today);
  }

  // Filter out unpublished classes for public viewers
  if (!includeUnpublished) {
    // Exclude drafts at query level (efficient)
    // Scheduled and private filtering is done client-side using isClassPubliclyVisible
    ownedQuery = ownedQuery.neq("status", "draft");
  }

  const { data: rawOwnedClasses, error: ownedError } = await ownedQuery;

  if (ownedError) {
    console.error("Error fetching owned classes:", ownedError);
    throw new Error(`Failed to fetch classes: ${ownedError.message}`);
  }

  // Filter out classes that shouldn't be publicly visible (for public viewers)
  // Uses centralized isClassPubliclyVisible check for consistency
  let ownedClasses = rawOwnedClasses || [];
  if (!includeUnpublished) {
    ownedClasses = ownedClasses.filter(c => isClassPubliclyVisible(c));
  }

  // 2. Fetch accepted collaborators for owned classes
  const ownedClassIds = ownedClasses.map(c => c.id);
  const collaboratorsMap: Record<string, CollaboratorProfileInfo[]> = {};

  if (ownedClassIds.length > 0) {
    const { data: collaboratorsData, error: collaboratorsError } = await supabase
      .from("collab_class_invites")
      .select(`
        class_id,
        invitee:profiles!invitee_profile_id (
          id,
          username,
          full_name,
          profile_photo
        )
      `)
      .in("class_id", ownedClassIds)
      .eq("status", "accepted");

    if (collaboratorsError) {
      console.error("Error fetching collaborators for owned classes:", collaboratorsError);
      // Don't throw - just continue without collaborator data
    } else if (collaboratorsData) {
      // Group collaborators by class_id
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      collaboratorsData.forEach((item: any) => {
        if (item.invitee) {
          if (!collaboratorsMap[item.class_id]) {
            collaboratorsMap[item.class_id] = [];
          }
          collaboratorsMap[item.class_id].push(item.invitee);
        }
      });
    }
  }

  // 3. Fetch collaborative classes (classes where user is an accepted collaborator)
  // Include owner profile info with payment handles and rate_info for pricing
  const { data: collaborations, error: collabError } = await supabase
    .from("collab_class_invites")
    .select(`
      id,
      class_id,
      classes (
        *,
        rate_info:class_rates!rate_id(id, name, price),
        series:series!series_id(id, title, profile:profiles!profile_id(id, username, full_name, profile_photo)),
        room_info:rooms!room_id(id, name, capacity, photos),
        owner:profiles!classes_profile_id_fkey (
          id,
          username,
          full_name,
          profile_photo,
          venmo_handle,
          cashapp_handle,
          zelle_handle,
          paypal_handle
        )
      )
    `)
    .eq("invitee_profile_id", profileId)
    .eq("status", "accepted");

  if (collabError) {
    console.error("Error fetching collaborative classes:", collabError);
    // Don't throw - just continue with owned classes only
  }

  // Extract collaborative classes and mark them, including owner profile
  // Transform rate_info from array to object (Supabase returns joins as arrays)
  const collaborativeClasses: ClassData[] = (collaborations || [])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .filter((c: any) => c.classes !== null)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((c: any) => {
      const { owner, rate_info, series, ...classData } = c.classes;
      // Supabase FK embeds can return either a single object or a 1-element array
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const seriesData: any = Array.isArray(series) ? series[0] ?? null : series ?? null;
      const seriesProfile = seriesData?.profile
        ? (Array.isArray(seriesData.profile) ? seriesData.profile[0] ?? null : seriesData.profile)
        : null;
      const seriesInfo = seriesData
        ? {
            id: seriesData.id as string,
            title: seriesData.title as string,
            profile: seriesProfile
              ? {
                  id: seriesProfile.id as string,
                  username: (seriesProfile.username ?? null) as string | null,
                  full_name: (seriesProfile.full_name ?? null) as string | null,
                  profile_photo: (seriesProfile.profile_photo ?? null) as string | null,
                }
              : null,
          }
        : null;
      return {
        ...classData,
        series: seriesInfo,
        series_owner_id: seriesProfile?.id ?? null,
        rate_info: Array.isArray(rate_info) ? rate_info[0] ?? null : rate_info ?? null,
        isCollaborator: true,
        ownerProfile: owner || undefined,
        collabInviteId: c.id,
      };
    })
    .filter((c: ClassData) => {
      // Filter by date based on upcomingOnly
      const dateFilter = upcomingOnly ? c.date >= today : c.date < today;
      if (!dateFilter) return false;

      // Filter out classes that shouldn't be publicly visible (for public viewers)
      if (!includeUnpublished && !isClassPubliclyVisible(c)) {
        return false;
      }

      return true;
    });

  // 3b. Fetch collaborators for collaborative classes (other collaborators, excluding self)
  const collaborativeClassIds = collaborativeClasses.map(c => c.id);
  const collabClassCollaboratorsMap: Record<string, CollaboratorProfileInfo[]> = {};

  if (collaborativeClassIds.length > 0) {
    const { data: collabCollaboratorsData, error: collabCollaboratorsError } = await supabase
      .from("collab_class_invites")
      .select(`
        class_id,
        invitee:profiles!invitee_profile_id (
          id,
          username,
          full_name,
          profile_photo
        )
      `)
      .in("class_id", collaborativeClassIds)
      .neq("invitee_profile_id", profileId) // Exclude current user
      .eq("status", "accepted");

    if (collabCollaboratorsError) {
      console.error("Error fetching collaborators for collaborative classes:", collabCollaboratorsError);
      // Don't throw - just continue without collaborator data
    } else if (collabCollaboratorsData) {
      // Group collaborators by class_id
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      collabCollaboratorsData.forEach((item: any) => {
        if (item.invitee) {
          if (!collabClassCollaboratorsMap[item.class_id]) {
            collabClassCollaboratorsMap[item.class_id] = [];
          }
          collabClassCollaboratorsMap[item.class_id].push(item.invitee);
        }
      });
    }
  }

  // 4. Merge all classes (before sorting)
  // Transform rate_info from array to object (Supabase returns joins as arrays)
  const allClasses: ClassData[] = [
    ...(ownedClasses || []).map(c => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const seriesRaw = (c as any).series;
      // Supabase FK embeds can return either a single object or a 1-element array
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const seriesData: any = Array.isArray(seriesRaw) ? seriesRaw[0] ?? null : seriesRaw ?? null;
      const seriesProfile = seriesData?.profile
        ? (Array.isArray(seriesData.profile) ? seriesData.profile[0] ?? null : seriesData.profile)
        : null;
      const seriesInfo = seriesData
        ? {
            id: seriesData.id as string,
            title: seriesData.title as string,
            profile: seriesProfile
              ? {
                  id: seriesProfile.id as string,
                  username: (seriesProfile.username ?? null) as string | null,
                  full_name: (seriesProfile.full_name ?? null) as string | null,
                  profile_photo: (seriesProfile.profile_photo ?? null) as string | null,
                }
              : null,
          }
        : null;
      return {
        ...c,
        series: seriesInfo,
        series_owner_id: seriesProfile?.id ?? null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rate_info: Array.isArray((c as any).rate_info) ? (c as any).rate_info[0] ?? null : (c as any).rate_info ?? null,
        isCollaborator: false,
        collaborators: collaboratorsMap[c.id] || [],
      };
    }),
    ...collaborativeClasses.map(c => ({
      ...c,
      collaborators: collabClassCollaboratorsMap[c.id] || [],
    })),
  ];

  // 5. Fetch registration counts for internal registration and studio-managed classes
  const internalClassIds = allClasses
    .filter(c => c.registration_type === 'internal' || c.is_studio_managed)
    .map(c => c.id);

  const registrationCountsMap: Record<string, number> = {};

  if (internalClassIds.length > 0) {
    // Get confirmed registration counts for each class
    const { data: registrationCounts, error: countsError } = await supabase
      .from("class_registrations")
      .select("class_id")
      .in("class_id", internalClassIds)
      .eq("status", "confirmed")
      .eq("is_on_waitlist", false);

    if (countsError) {
      console.error("Error fetching registration counts:", countsError);
      // Don't throw - just continue without counts
    } else if (registrationCounts) {
      // Count registrations per class
      registrationCounts.forEach((reg) => {
        registrationCountsMap[reg.class_id] = (registrationCountsMap[reg.class_id] || 0) + 1;
      });
    }
  }

  // 6. Add registration counts to classes
  const classesWithCounts = allClasses.map(c => ({
    ...c,
    registration_count: (c.registration_type === 'internal' || c.is_studio_managed)
      ? (registrationCountsMap[c.id] || 0)
      : undefined,
  }));

  // Sort by date and time
  classesWithCounts.sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) {
      return upcomingOnly ? dateCompare : -dateCompare;
    }
    const timeCompare = a.start_time.localeCompare(b.start_time);
    return upcomingOnly ? timeCompare : -timeCompare;
  });

  // Final safety filter using centralized visibility check
  // (Primary filtering is done earlier for owned and collaborative classes separately)
  if (!includeUnpublished) {
    return classesWithCounts.filter(c => isClassPubliclyVisible(c));
  }

  return classesWithCounts;
}

export interface RecentStudio {
  location: string;
  address: string | null;
}

/**
 * Fetch distinct recent studios from the user's past classes.
 * Returns up to 3 unique location+address pairs, ordered by most recently created.
 */
export async function getRecentStudios(
  profileId: string
): Promise<RecentStudio[]> {
  const { data, error } = await supabase
    .from("classes")
    .select("location, address")
    .eq("profile_id", profileId)
    .order("created_at", { ascending: false })
    .limit(20);

  if (error || !data) {
    return [];
  }

  const seen = new Set<string>();
  const unique: RecentStudio[] = [];
  for (const row of data) {
    const key = row.location.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      unique.push({ location: row.location, address: row.address });
      if (unique.length >= 5) break;
    }
  }

  return unique;
}

export interface LastClassPaymentMethods {
  card_payment_enabled: boolean;
  venmo_enabled: boolean;
  cashapp_enabled: boolean;
  zelle_enabled: boolean;
  paypal_enabled: boolean;
}

/**
 * Fetch the most recent class's payment method preferences for a user
 * Used to pre-populate the add class form with last used settings
 * @param profileId Profile UUID
 * @returns Payment method preferences from the most recent class, or null if no classes exist
 */
export async function getLastClassPaymentMethods(
  profileId: string
): Promise<LastClassPaymentMethods | null> {
  const { data, error } = await supabase
    .from("classes")
    .select("card_payment_enabled, venmo_enabled, cashapp_enabled, zelle_enabled, paypal_enabled")
    .eq("profile_id", profileId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) {
    // No classes found or error - return null to use defaults
    return null;
  }

  return {
    card_payment_enabled: data.card_payment_enabled ?? false,
    venmo_enabled: data.venmo_enabled ?? true,
    cashapp_enabled: data.cashapp_enabled ?? true,
    zelle_enabled: data.zelle_enabled ?? true,
    paypal_enabled: data.paypal_enabled ?? true,
  };
}

/**
 * Create a new class
 *
 * Routes through the backend POST /classes endpoint so the repository-layer
 * dual-write (price_cents, etc.) runs. The backend infers profile_id from the
 * authenticated session.
 *
 * @param classData Class information
 * @returns Created class data
 */
export async function createClass(classData: CreateClassInput): Promise<ClassData> {
  return api.post<ClassData>('/classes', classData);
}

/**
 * Update an existing class
 * @param classId Class UUID
 * @param updates Partial class data to update
 * @returns Updated class data
 */
export async function updateClass(
  classId: string,
  updates: UpdateClassInput
): Promise<ClassData> {
  return api.put<ClassData>(`/classes/${classId}`, updates);
}

/**
 * Delete a class
 * @param classId Class UUID
 */
export async function deleteClass(classId: string): Promise<void> {
  const { error } = await supabase.from("classes").delete().eq("id", classId);

  if (error) {
    console.error("Error deleting class:", error);
    throw new Error(`Failed to delete class: ${error.message}`);
  }
}

/**
 * Get a single class by ID
 * @param classId Class UUID
 * @returns Class data
 */
export async function getClassById(classId: string): Promise<ClassData> {
  const { data, error } = await supabase
    .from("classes")
    .select("*")
    .eq("id", classId)
    .single();

  if (error) {
    console.error("Error fetching class:", error);
    throw new Error(`Failed to fetch class: ${error.message}`);
  }

  return data;
}

// Extended class type with profile information for discovery
export interface ClassWithProfile extends ClassData {
  profile: {
    id: string;
    username: string | null;
    full_name: string | null;
    profile_photo: string | null;
    instagram_handle: string | null;
    venmo_handle: string | null;
    cashapp_handle: string | null;
    zelle_handle: string | null;
    paypal_handle: string | null;
  } | null;
  collaborators?: CollaboratorProfileInfo[];
  isCollaborator?: boolean; // True if current user is a collaborator (not owner)
}

/** Response from GET /classes/:id — full class detail with profile and registration status */
export interface ClassDetailResponse extends ClassData {
  profile: {
    id: string;
    username: string | null;
    full_name: string | null;
    profile_photo: string | null;
    instagram_handle: string | null;
    venmo_handle: string | null;
    cashapp_handle: string | null;
    zelle_handle: string | null;
    paypal_handle: string | null;
    class_refunds_allowed: boolean | null;
    class_no_refund_hours: number | null;
  } | null;
  collaborators: {
    id: string;
    profile_id: string;
    full_name: string | null;
    username: string | null;
    profile_photo: string | null;
  }[];
  user_registration: {
    id: string;
    is_on_waitlist: boolean;
    payment_method: string | null;
    is_paid: boolean;
    payment_intent_id: string | null;
    payment_status: string | null;
    payment_amount: number | null;
    payment_code: string | null;
  } | null;
  studio_class_settings: StudioClassSettingsResponse | null;
}

/**
 * Fetch full class detail from backend API (includes profile, collaborators, registration status)
 */
export async function fetchClassDetail(classId: string, opts?: { skipAuth?: boolean }): Promise<ClassDetailResponse> {
  return api.get<ClassDetailResponse>(`/classes/${classId}`, opts?.skipAuth ? { skipAuth: true } : undefined);
}

export interface ClassDiscoveryResponse {
  classes: ClassWithProfile[];
  total: number;
  has_more: boolean;
}

export interface ClassDiscoveryParams {
  limit?: number;
  offset?: number;
  from_date?: string;
  to_date?: string;
  status?: ClassStatus;
  instructor_id?: string;
  sort?: 'date_asc' | 'date_desc';
}

/**
 * Fetch all upcoming classes across all instructors for discovery
 * Uses the backend API endpoint for proper validation and business logic
 * @param params Query parameters for filtering and pagination
 * @returns Discovery response with classes and pagination metadata
 */
export async function fetchAllUpcomingClasses(
  params: ClassDiscoveryParams = {}
): Promise<ClassDiscoveryResponse> {
  // Import API_URL dynamically to avoid circular dependencies
  const { API_URL } = await import("@/lib/api/api-client");

  // Build query string
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.offset) queryParams.append('offset', params.offset.toString());
  if (params.from_date) queryParams.append('from_date', params.from_date);
  if (params.to_date) queryParams.append('to_date', params.to_date);
  if (params.status) queryParams.append('status', params.status);
  if (params.instructor_id) queryParams.append('instructor_id', params.instructor_id);
  if (params.sort) queryParams.append('sort', params.sort);

  const url = `${API_URL}/classes/discover${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Error fetching all upcoming classes:", errorText);
    throw new Error(`Failed to fetch upcoming classes: ${response.statusText}`);
  }

  const data: ClassDiscoveryResponse = await response.json();
  return data;
}

/**
 * Fetch upcoming classes from favorite instructors only
 * Requires authentication to get user's favorites
 * @param params Query parameters for filtering and pagination
 * @returns Discovery response with classes from favorite instructors
 */
export async function fetchFavoriteInstructorsClasses(
  params: Omit<ClassDiscoveryParams, 'instructor_id'> = {}
): Promise<ClassDiscoveryResponse> {
  // Import API dynamically to avoid circular dependencies
  const { api } = await import("@/lib/api/api-client");

  // Build query string
  const queryParams = new URLSearchParams();
  queryParams.append('favorite_instructors_only', 'true');
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.offset) queryParams.append('offset', params.offset.toString());
  if (params.from_date) queryParams.append('from_date', params.from_date);
  if (params.to_date) queryParams.append('to_date', params.to_date);
  if (params.status) queryParams.append('status', params.status);
  if (params.sort) queryParams.append('sort', params.sort);

  const path = `/classes/discover${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

  try {
    const data = await api.get<ClassDiscoveryResponse>(path);
    return data;
  } catch (error) {
    console.error("Error fetching favorite instructors' classes:", error);
    // Return empty response if error (e.g., not authenticated)
    return {
      classes: [],
      total: 0,
      has_more: false
    };
  }
}
